'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type NotificationType = 'order_status' | 'approval' | 'low_stock' | 'invoice_payment';

export interface NotificationPreferences {
  order_status: boolean;
  approval: boolean;
  low_stock: boolean;
  invoice_payment: boolean;
}

export interface ActivityLogEntry {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  icon: string;
  color: string;
}

export interface ToastItem {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  variant: 'success' | 'warning' | 'info' | 'error';
}

interface NotificationContextValue {
  preferences: NotificationPreferences;
  setPreferences: React.Dispatch<React.SetStateAction<NotificationPreferences>>;
  activityLog: ActivityLogEntry[];
  toasts: ToastItem[];
  unreadCount: number;
  showLogPanel: boolean;
  setShowLogPanel: (v: boolean) => void;
  addNotification: (
    type: NotificationType,
    title: string,
    message: string,
    variant?: ToastItem['variant']
  ) => void;
  markAllRead: () => void;
  clearLog: () => void;
}

const PREF_STORAGE_KEY = 'fbs_notification_prefs';
const LOG_STORAGE_KEY = 'fbs_activity_log';

const DEFAULT_PREFS: NotificationPreferences = {
  order_status: true,
  approval: true,
  low_stock: true,
  invoice_payment: true,
};

const TYPE_META: Record<NotificationType, { icon: string; color: string }> = {
  order_status: { icon: 'ClipboardDocumentListIcon', color: 'text-blue-600' },
  approval: { icon: 'ShieldCheckIcon', color: 'text-purple-600' },
  low_stock: { icon: 'ExclamationTriangleIcon', color: 'text-amber-600' },
  invoice_payment: { icon: 'BanknotesIcon', color: 'text-green-600' },
};

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFS);
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [showLogPanel, setShowLogPanel] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Load persisted prefs and log on mount
  useEffect(() => {
    setMounted(true);
    try {
      const storedPrefs = localStorage.getItem(PREF_STORAGE_KEY);
      if (storedPrefs) setPreferences(JSON.parse(storedPrefs));
      const storedLog = localStorage.getItem(LOG_STORAGE_KEY);
      if (storedLog) setActivityLog(JSON.parse(storedLog));
    } catch {
      // ignore
    }
  }, []);

  // Persist prefs
  useEffect(() => {
    if (!mounted) return;
    try { localStorage.setItem(PREF_STORAGE_KEY, JSON.stringify(preferences)); } catch { /* ignore */ }
  }, [preferences, mounted]);

  // Persist log
  useEffect(() => {
    if (!mounted) return;
    try { localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(activityLog.slice(0, 100))); } catch { /* ignore */ }
  }, [activityLog, mounted]);

  const unreadCount = activityLog.filter(e => !e.read).length;

  const addNotification = useCallback((
    type: NotificationType,
    title: string,
    message: string,
    variant: ToastItem['variant'] = 'info'
  ) => {
    // Always add to activity log regardless of preference
    const entry: ActivityLogEntry = {
      id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type,
      title,
      message,
      timestamp: new Date().toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      }),
      read: false,
      icon: TYPE_META[type].icon,
      color: TYPE_META[type].color,
    };
    setActivityLog(prev => [entry, ...prev].slice(0, 100));

    // Only show toast if preference is enabled
    setPreferences(currentPrefs => {
      if (currentPrefs[type]) {
        const toast: ToastItem = {
          id: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          type,
          title,
          message,
          variant,
        };
        setToasts(prev => [...prev, toast]);
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== toast.id));
        }, 4500);
      }
      return currentPrefs;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setActivityLog(prev => prev.map(e => ({ ...e, read: true })));
  }, []);

  const clearLog = useCallback(() => {
    setActivityLog([]);
  }, []);

  return (
    <NotificationContext.Provider value={{
      preferences,
      setPreferences,
      activityLog,
      toasts,
      unreadCount,
      showLogPanel,
      setShowLogPanel,
      addNotification,
      markAllRead,
      clearLog,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
